import React, { useState } from 'react';
import { getAvatarUrlWithCacheBuster } from '../utils/avatar.ts';

interface UserAvatarProps {
  src?: string | null;
  alt?: string;
  name?: string;
  sizeClassName?: string;
  textClassName?: string;
  className?: string;
  versionKey?: number | string;
  onClick?: () => void;
  title?: string;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  src,
  alt = 'Scholar',
  name = 'Scholar',
  sizeClassName = 'w-10 h-10',
  textClassName = 'text-xs font-bold text-amber-400',
  className = '',
  versionKey,
  onClick,
  title,
}) => {
  const [hasError, setHasError] = useState(false);

  // Compute initials fallback (e.g. "Adonis Melencion" -> "AM", "scholar" -> "SC")
  const cleanName = (name || alt || 'Scholar').trim();
  const parts = cleanName.split(/\s+/);
  let initials = 'SC';
  if (parts.length >= 2 && parts[0] && parts[1]) {
    initials = (parts[0][0] + parts[1][0]).toUpperCase();
  } else if (cleanName.length >= 2) {
    initials = cleanName.slice(0, 2).toUpperCase();
  } else if (cleanName.length === 1) {
    initials = cleanName.toUpperCase();
  }

  const renderSrc = getAvatarUrlWithCacheBuster(src, versionKey);

  if (!renderSrc || hasError) {
    return (
      <div
        onClick={onClick}
        title={title}
        className={`${sizeClassName} rounded-full bg-[#1C1C1C] border border-[#404040] flex items-center justify-center shrink-0 ${onClick ? 'cursor-pointer hover:border-amber-500/50 transition-colors' : ''} ${className}`}
        aria-label={`${cleanName} avatar initials (${initials})`}
      >
        <span className={textClassName}>{initials}</span>
      </div>
    );
  }

  return (
    <img
      src={renderSrc}
      alt={alt}
      title={title}
      onClick={onClick}
      onError={() => {
        // Requirement 6: Fall back to initials circle on error, but DO NOT clear avatar_url in DB!
        setHasError(true);
      }}
      className={`${sizeClassName} rounded-full object-cover border border-[#404040] shrink-0 ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''} ${className}`}
    />
  );
};
